// ** Course player layout (lesson area, playlist, details)

import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";

// ** MUI Imports
import Box from "@mui/material/Box";
import FormatListBulleted from "mdi-material-ui/FormatListBulleted";
import ChevronLeft from "mdi-material-ui/ChevronLeft";
// ** Components Imports
import AppBarHeader from "../components/coursePlayer/Header/AppBarHeader";
import Details from "../components/coursePlayer/Details";
import LessonContent from "../components/coursePlayer/LessonContent/LessonContent";
import TransactionInfo from "./TransactionInfo";
import Comments from "../components/Comments";
import DesktopPlaylist from "../components/coursePlayer/PlayList/DesktopPlaylist";
import MobilePlaylist from "../components/coursePlayer/PlayList/MobilePlaylist";
import { useAuth } from "src/hooks/useAuth";
import Tooltip from "@mui/material/Tooltip";

function CoursePlayerLayout(props) {
  const {
    settings,
    saveSettings,
    data,
    videos_info,
    actualChapter,
    actualLesson,
    lessonStatusHandler,
    activeLessonHandler,
    updateVideoCurrentTime,
    updateAudioCurrentTime,
    lessonsStatus,
    moveToNextLesson,
    moveToPreviousLesson,
    isFirstLesson,
    isLastLesson,
    refreshLessonsStatus,
  } = props;
  const { course, details } = data;
  const auth = useAuth();
  const [searchParams] = useSearchParams();
  const isIframeMode = searchParams.get("mode") === "iframe";
  const isSmallDevice = window.innerWidth < 1400;

  const [tab, setTab] = useState("1"); //Guarda valor da tab atual
  const [collapsedList, setCollapsedList] = useState(false); //Guarda valor do estado da lista de reprodução
  const updateVideoCurrentTimeHandlerMiddleware = (video_id, currentTime) => {
    updateVideoCurrentTime(actualChapter, actualLesson, video_id, currentTime);
  };

  const updateAudioCurrentTimeHandlerMiddleware = (lesson_id, currentTime) => {
    updateAudioCurrentTime(actualLesson, lesson_id, currentTime);
  };

  const onShowAttachmentsClickHandler = () => {
    //console.log('onShowAttachmentsClickHandler')
    setTab("2");
  };

  const onCollapseListClickHandler = () => {
    // console.log("onCollapseListClickHandler");
    setCollapsedList(!collapsedList);
  };

  let activeChapter = details?.find((detail) => detail.chapter_id === actualChapter);
  let activeChapterIndex = details?.findIndex((detail) => detail.chapter_id === actualChapter);

  if (activeChapter === undefined) activeChapter = details[0];

  let activeLesson = details?.find((detail) => detail.chapter_id === actualChapter)?.lessons?.find((lesson) => lesson.lesson_id === actualLesson);

  if (activeLesson === undefined) {
    activeLesson = activeChapter?.lessons[0];
    activeLessonHandler(activeChapter.chapter_id, activeLesson.lesson_id);
  }

  const updateLessonAsCompleted = () => {
    const isQuestionnaire = activeLesson?.lesson_type === "questionnaire";
    lessonStatusHandler(actualChapter, actualLesson, true, isQuestionnaire === true ? { fromQuestionnaireSubmit: true } : {});
  };

  const isLastChapter = details[details.length - 1]?.chapter_id === actualChapter;
  const lessonsList = details
    ?.map((chapter) =>
      chapter.lessons?.map((lesson) => ({
        ...lesson,
        chapter_id: chapter.chapter_id,
      })),
    )
    .flat();

  //const isLastLesson = _lessons[_lessons.length - 1]?.lesson_id === actualLesson;

  // console.log("isLastChapter", isLastChapter);
  return (
    <>
      {!isIframeMode && (
        <AppBarHeader
          lessons={lessonsList}
          collapsedList={collapsedList}
          title={course?.title}
          settings={settings}
          saveSettings={saveSettings}
          whiteLogo={course?.brand_logo_white}
          darkLogo={course?.brand_logo_dark || course?.brand_logo_white}
          activeLessonHandler={activeLessonHandler}
        />
      )}

      {/*"Header" da página*/}
      {collapsedList && !isSmallDevice && (
        <div className="collapsed-button">
          {/* <Button onClick={onCollapseListClickHandler}>Expandir lista</Button> */}
          <Tooltip title="Mostrar lista de conteúdos" placement="left">
            <button type="button" className="playlist-expand-tab" aria-label="Mostrar lista de conteúdos" onClick={() => onCollapseListClickHandler()}>
              <FormatListBulleted />
              <ChevronLeft />
            </button>
          </Tooltip>
        </div>
      )}
      <Box>
        <div className={`frame-left ${collapsedList || isSmallDevice ? "collapsed-list" : ""}`}>
          <TransactionInfo course={course} />
          <LessonContent
            isSmallDevice={isSmallDevice}
            activeChapter={activeChapter}
            courseAddedAt={course?.added_at}
            courseEndDate={course?.ended_at}
            isLastChapter={isLastChapter}
            isLastLesson={isLastLesson}
            moveToNextLesson={moveToNextLesson}
            updateLessonAsCompleted={updateLessonAsCompleted}
            refreshLessonsStatus={refreshLessonsStatus}
            course={course}
            videos_info={videos_info}
            details={activeLesson}
            lessonsStatus={lessonsStatus}
            updateVideoCurrentTimeHandler={updateVideoCurrentTimeHandlerMiddleware}
            updateAudioCurrentTimeHandler={updateAudioCurrentTimeHandlerMiddleware}
          />
          <Details
            authData={auth?.user}
            details={activeLesson}
            tab={tab}
            setTab={setTab}
            activeChapter={activeChapter}
            activeChapterIndex={activeChapterIndex}
            courseAddedAt={course?.added_at}
            showChapterNumbers={course?.show_chapter_numbers}
            hasTeaserAccess={course?.teaser_access === true}
          />
          {/* Comentários */}
          {course?.allow_comments && (
            <Comments
              activeChapter={activeChapter}
              courseAddedAt={course?.added_at}
              isSmallDevice={isSmallDevice}
              courseId={course?.course_id}
              userCanReply={course?.user_can_reply}
              chapterId={activeChapter.chapter_id}
              lessonId={activeLesson.lesson_id}
              auth={auth}
            />
          )}
        </div>
      </Box>
      {!isSmallDevice && (
        <>
          {!collapsedList && (
            <div className={`frame-right ${collapsedList ? "collapsed-list" : ""}`}>
              <DesktopPlaylist
                isFirstLesson={isFirstLesson}
                isLastLesson={isLastLesson}
                onCollapseListClick={onCollapseListClickHandler}
                courseAddedAt={course?.added_at}
                courseEndDate={course?.ended_at}
                lessonsStatus={lessonsStatus}
                videos_info={videos_info}
                actualChapter={actualChapter}
                actualLesson={actualLesson}
                details={details}
                lessonStatusHandler={lessonStatusHandler}
                showLessonNumbers={course?.show_lesson_numbers}
                showChapterNumbers={course?.show_chapter_numbers}
                hasTeaserAccess={course?.teaser_access === true}
                activeLessonHandler={(chapter, lesson_id) => {
                  activeLessonHandler(chapter, lesson_id);
                  if (isSmallDevice) onCollapseListClickHandler();
                }}
                onShowAttachmentsClick={onShowAttachmentsClickHandler}
              />
            </div>
          )}
        </>
      )}
      {isSmallDevice && (
        <MobilePlaylist
          isFirstLesson={isFirstLesson}
          isLastLesson={isLastLesson}
          courseAddedAt={course?.added_at}
          courseEndDate={course?.ended_at}
          lessonsStatus={lessonsStatus}
          videos_info={videos_info}
          actualChapter={actualChapter}
          actualLesson={actualLesson}
          details={details}
          moveToPreviousLesson={moveToPreviousLesson}
          moveToNextLesson={moveToNextLesson}
          lessonStatusHandler={lessonStatusHandler}
          showLessonNumbers={course?.show_lesson_numbers}
          showChapterNumbers={course?.show_chapter_numbers}
          hasTeaserAccess={course?.teaser_access === true}
          activeLessonHandler={(chapter, lesson_id) => {
            activeLessonHandler(chapter, lesson_id);
            if (isSmallDevice) onCollapseListClickHandler();
          }}
          onShowAttachmentsClick={onShowAttachmentsClickHandler}
        />
      )}
    </>
  );
}

export default CoursePlayerLayout;
